import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { installSoftKeyboardWatcher } from '../utils/softKeyboard';

export const SUPPORT_EMAIL = 'atendimento@consultamais.app';
export const SUPPORT_MAILTO = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent('Suporte - Consulta+')}`;

export const COMPANY_FANTASIA = 'CONSULTA MAIS TELEMEDICINA';
export const COMPANY_LEGAL_NAME = 'Esmanhotto Serviços Médicos LTDA';
export const COMPANY_CNPJ = '57.946.158/0001-76';
export const COMPANY_ADDRESS =
  'Quadra CRS 502, Bloco C, Loja 37, Parte 4243, Asa Sul, Brasília/DF, CEP 70330-530';
// Res. CFM 2.336/2023, art. 5º, I, "a" e "b": registro da empresa e Diretor Técnico-Médico no CRM.
export const COMPANY_CRM_REGISTRATION = 'Registro CRM-DF-PJ: 10.300';
export const TECHNICAL_DIRECTOR_LINE =
  'Diretor Técnico: Guilherme Esmanhotto · Médico: CRM-DF 23.706';

const FOOTER_MOUNT_ID = 'patient-support-footer-root';
const KEYBOARD_INSET_PX = 80;

const FOOTER_STYLES = `
  .patient-support-footer {
    position: fixed !important;
    left: 0;
    right: 0;
    bottom: 0;
    bottom: env(safe-area-inset-bottom, 0px);
    z-index: 45;
    width: 100%;
    max-width: 100vw;
    box-sizing: border-box;
    text-align: center;
    padding: 0.45rem 0.75rem calc(0.45rem + env(safe-area-inset-bottom, 0px));
    font-size: 0.75rem;
    line-height: 1.35;
    color: #6B7280;
    background: #F9FAFB;
    border-top: 1px solid rgba(0, 0, 0, 0.06);
    pointer-events: none;
  }
  .patient-support-footer a {
    pointer-events: auto;
    color: #B00000;
    font-weight: 600;
    text-decoration: none;
    overflow-wrap: break-word;
  }
  .patient-support-footer a:hover,
  .patient-support-footer a:focus-visible {
    text-decoration: underline;
    outline: none;
  }
  .patient-support-footer__legal {
    margin: 0.15rem 0 0;
    font-size: 0.68rem;
    line-height: 1.35;
    font-weight: 400;
    color: #000;
  }
  .patient-support-footer__legal strong {
    font-weight: 500;
    color: inherit;
    letter-spacing: 0.01em;
  }
  .patient-support-footer__nowrap {
    white-space: nowrap;
  }
  /* Com o teclado aberto o rodapé fixo cobre o campo focado (no app Android
     ele sobe junto com a janela). Volta quando o teclado fecha. */
  html.soft-keyboard-open .patient-support-footer:not(.patient-support-footer--static) {
    visibility: hidden;
  }
  .patient-support-footer--static {
    position: static !important;
    z-index: auto;
    background: transparent;
    border-top: none;
    pointer-events: auto;
    padding: 0.75rem 0.75rem calc(0.25rem + env(safe-area-inset-bottom, 0px));
    font-size: 0.8rem;
    transform: none;
  }
  /* As regras de body.has-patient-support-footer (padding-bottom, page-shell,
     chat-wrapper, str-chat__message-input) ficam inline no <head> do index.html
     desde 0.1.89 para evitar CLS no primeiro paint (auditoria UX 13/06/2026). */
`;

function getFooterMountNode() {
  if (typeof document === 'undefined') return null;
  return document.getElementById(FOOTER_MOUNT_ID) || document.body;
}

export default function PatientSupportFooter({ variant = 'fixed', hideCnpj = false }) {
  const isStatic = variant === 'static';
  const [portalReady, setPortalReady] = useState(false);
  const footerRef = useRef(null);

  useEffect(() => {
    setPortalReady(true);
    installSoftKeyboardWatcher();
  }, []);

  // CLS-fix 13/06/2026: a classe `has-patient-support-footer` agora vem
  // direto no <body> do index.html (reserva de padding no primeiro paint).
  // Toggle via JS causaria layout-shift visivel — mantemos no-op aqui para
  // documentar a intencao e nao quebrar referencias externas a classe.

  // iOS Safari: visualViewport.offsetTop muda no scroll e desloca bottom em CSS.
  // Só elevamos o rodape quando o inset indica teclado virtual (nao scroll normal).
  useEffect(() => {
    if (isStatic || !portalReady) return undefined;
    const el = footerRef.current;
    if (!el || typeof window === 'undefined') return undefined;

    // Reposiciona por `transform`, não por `bottom`. Mudar `bottom` é layout
    // shift e entra no CLS — e como o handler também roda em `scroll` do
    // visualViewport (iOS muda offsetTop ao rolar), o shift não é atribuído a
    // input recente. Era a maior fonte de CLS do app (3.207 atribuições na
    // auditoria de 29/09/2026). Shifts por transform não contam para o CLS.
    const apply = () => {
      const vv = window.visualViewport;
      if (!vv) {
        el.style.transform = '';
        return;
      }
      const inset = Math.max(0, (window.innerHeight || 0) - vv.height - (vv.offsetTop || 0));
      el.style.transform = inset > KEYBOARD_INSET_PX ? `translate3d(0, -${inset}px, 0)` : '';
    };

    apply();
    const vv = window.visualViewport;
    vv?.addEventListener('resize', apply);
    vv?.addEventListener('scroll', apply);
    window.addEventListener('resize', apply);
    window.addEventListener('orientationchange', apply);

    return () => {
      vv?.removeEventListener('resize', apply);
      vv?.removeEventListener('scroll', apply);
      window.removeEventListener('resize', apply);
      window.removeEventListener('orientationchange', apply);
      if (el) el.style.transform = '';
    };
  }, [isStatic, portalReady]);

  const footer = (
    <footer
      ref={footerRef}
      className={`patient-support-footer${isStatic ? ' patient-support-footer--static' : ''}`}
      role="contentinfo"
      aria-label="Identificação da empresa e contato de suporte"
    >
      <div>
        Problemas?{' '}
        <a
          href={SUPPORT_MAILTO}
          aria-label={`Enviar e-mail para ${SUPPORT_EMAIL}`}
          title={SUPPORT_EMAIL}
        >
          {SUPPORT_EMAIL}
        </a>
      </div>
      {!isStatic && (
        <p className="patient-support-footer__legal">
          <strong>{COMPANY_FANTASIA}</strong>
          {!hideCnpj && (
            <>
              {' · '}
              <span className="patient-support-footer__nowrap">CNPJ {COMPANY_CNPJ}</span>
            </>
          )}
          {' · '}
          <span className="patient-support-footer__nowrap">{COMPANY_CRM_REGISTRATION}</span>
          {' · '}
          {TECHNICAL_DIRECTOR_LINE}
          {' · '}
          {COMPANY_ADDRESS}
        </p>
      )}
    </footer>
  );

  const mountNode = portalReady ? getFooterMountNode() : null;

  return (
    <>
      <style>{FOOTER_STYLES}</style>
      {isStatic
        ? footer
        : mountNode
          ? createPortal(footer, mountNode)
          : null}
    </>
  );
}